import React, { useState, useEffect } from 'react';
import { ordersService } from '../services/ordersService';
import { productsService } from '../services/productsService';
import { contactService } from '../services/contactService';
import {
  TrendingUp,
  ShoppingCart,
  Package,
  MessageSquare,
  DollarSign,
  Clock,
  CheckCircle
} from 'lucide-react';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    orders: { total: 0, pending: 0, completed: 0 },
    products: { total: 0 },
    contacts: { total: 0, new: 0 },
    revenue: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch orders
      const ordersResponse = await ordersService.getOrders();
      const orders = ordersResponse.success ? ordersResponse.data : [];
      
      // Fetch products
      const productsResponse = await productsService.getProducts();
      const products = productsResponse.success ? productsResponse.data : [];
      
      // Fetch contacts
      const contactsResponse = await contactService.getContacts();
      const contacts = contactsResponse.success ? contactsResponse.data : [];

      // Calculate statistics
      const pendingOrders = orders.filter(order => order.order_status === 'pending').length;
      const completedOrders = orders.filter(order => order.order_status === 'completed').length;
      const newContacts = contacts.filter(contact => contact.status === 'new').length;
      const totalRevenue = orders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);

      setStats({
        orders: { 
          total: orders.length, 
          pending: pendingOrders, 
          completed: completedOrders 
        },
        products: { total: products.length },
        contacts: { 
          total: contacts.length, 
          new: newContacts 
        },
        revenue: totalRevenue
      });

      // Set recent orders (last 5)
      setRecentOrders(orders.slice(0, 5));
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className={`stat-card ${color}`}>
      <div className="stat-icon">
        <Icon size={24} />
      </div>
      <div className="stat-content">
        <h3 className="stat-value">{value}</h3>
        <p className="stat-title">{title}</p>
        {subtitle && <p className="stat-subtitle">{subtitle}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Welcome to your admin panel</p>
      </div>

      <div className="stats-grid">
        <StatCard
          icon={ShoppingCart}
          title="Total Orders"
          value={stats.orders.total}
          subtitle={`${stats.orders.pending} pending, ${stats.orders.completed} completed`}
          color="blue"
        />
        <StatCard
          icon={Package}
          title="Products"
          value={stats.products.total}
          subtitle="Total products"
          color="green"
        />
        <StatCard
          icon={MessageSquare}
          title="Contact Messages"
          value={stats.contacts.total}
          subtitle={`${stats.contacts.new} new messages`}
          color="orange"
        />
        <StatCard
          icon={DollarSign}
          title="Total Revenue"
          value={`$${stats.revenue.toFixed(2)}`}
          subtitle="From all orders"
          color="purple"
        />
      </div>

      <div className="dashboard-content">
        <div className="recent-orders">
          <h2>Recent Orders</h2>
          {recentOrders.length > 0 ? (
            <div className="orders-table">
              <table>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map(order => (
                    <tr key={order.id}>
                      <td>#{order.id}</td>
                      <td>{order.name}</td>
                      <td>${parseFloat(order.total_amount || 0).toFixed(2)}</td>
                      <td>
                        <span className={`status ${order.order_status}`}>
                          {order.order_status === 'pending' && <Clock size={14} />}
                          {order.order_status === 'completed' && <CheckCircle size={14} />}
                          {order.order_status}
                        </span>
                      </td>
                      <td>{new Date(order.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">No recent orders</p>
          )}
        </div>

        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-buttons">
            <button className="action-btn">
              <ShoppingCart size={20} />
              View All Orders
            </button>
            <button className="action-btn">
              <Package size={20} />
              Manage Products
            </button>
            <button className="action-btn">
              <MessageSquare size={20} />
              Check Messages
            </button>
            <button className="action-btn">
              <TrendingUp size={20} />
              View Analytics
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;